
// frontend/src/services/dashboardStats.service.ts - Real-time Dashboard Statistics
import {
    collection,
    query,
    where,
    getDocs,
    orderBy,
    limit,
    Timestamp
  } from 'firebase/firestore';
  import { db } from './firebase';
  
  export interface RecentActivity {
    id: string;
    type: 'created' | 'approved' | 'rejected' | 'validated' | 'modified';
    projectName: string;
    projectCode: string;
    employeeCount: number;
    timestamp: Date;
    timeAgo: string;
    status: string;
    requestId: string;
    validationStatus?: 'pending' | 'complete' | 'partial' | 'failed';
  }
  
  export interface MonthlyStats {
    totalRequests: number;
    approvedRequests: number;
    pendingRequests: number;
    rejectedRequests: number;
    totalHours: number;
    totalEmployees: number;
    avgHoursPerRequest: number;
    validatedComplete: number;
    validatedWithIssues: number;
    pendingValidation: number;
  }
  
  export interface WeeklyTrend {
    day: string;
    date: string;
    requests: number;
    hours: number;
    employees: number;
  }
  
  class DashboardStatsService {
    /**
     * Get recent activity for user (real-time)
     */
    async getRecentActivity(userId: string, limitCount: number = 10): Promise<RecentActivity[]> {
      try {
        console.log('📊 Fetching recent activity for user:', userId);
  
        const activities: RecentActivity[] = [];
  
        // Query recent overtime requests by this user
        const recentQuery = query(
          collection(db, 'overtime_requests'),
          where('requesterId', '==', userId),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        );
  
        const snapshot = await getDocs(recentQuery);
  
        for (const doc of snapshot.docs) {
          const data = doc.data();
          const createdAt = data.createdAt?.toDate() || new Date();
          const updatedAt = data.updatedAt?.toDate() || createdAt;
  
          // Determine activity type based on status and timestamps
          let activityType: RecentActivity['type'] = 'created';
          let activityTimestamp = createdAt;
  
          if (data.status === 'approved') {
            activityType = 'approved';
            activityTimestamp = data.approvedAt?.toDate() || updatedAt;
          } else if (data.status === 'rejected') {
            activityType = 'rejected';
            activityTimestamp = data.rejectedAt?.toDate() || updatedAt;
          } else if (data.attendanceValidation?.isValidated) {
            activityType = 'validated';
            activityTimestamp = data.attendanceValidation?.validatedAt?.toDate() || updatedAt;
          }
  
          // Get validation status
          let validationStatus: RecentActivity['validationStatus'] = 'pending';
          if (data.attendanceValidation?.isValidated) {
            if (data.attendanceValidation.absentCount === 0) {
              validationStatus = 'complete';
            } else if (data.attendanceValidation.presentCount === 0) {
              validationStatus = 'failed';
            } else {
              validationStatus = 'partial';
            }
          }
  
          activities.push({
            id: doc.id,
            type: activityType,
            projectName: data.projectName || 'Unknown Project',
            projectCode: data.projectCode || '',
            employeeCount: data.totalEmployees || data.employeeIds?.length || 0,
            timestamp: activityTimestamp,
            timeAgo: this.getTimeAgo(activityTimestamp),
            status: data.status || 'pending',
            requestId: doc.id,
            validationStatus
          });
        }
  
        // Sort by timestamp (most recent first)
        activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  
        console.log(`✅ Found ${activities.length} recent activities`);
        return activities;
  
      } catch (error) {
        console.error('❌ Error fetching recent activity:', error);
        return [];
      }
    }
  
    /**
     * Get monthly statistics (real data)
     */
    async getMonthlyStats(userId: string): Promise<MonthlyStats> {
      try {
        console.log('📊 Fetching monthly stats for user:', userId);
  
        // Get start and end of current month
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  
        const monthQuery = query(
          collection(db, 'overtime_requests'),
          where('requesterId', '==', userId),
          where('createdAt', '>=', Timestamp.fromDate(startOfMonth)),
          where('createdAt', '<=', Timestamp.fromDate(endOfMonth))
        );
  
        const snapshot = await getDocs(monthQuery);
  
        let totalRequests = 0;
        let approvedRequests = 0;
        let pendingRequests = 0;
        let rejectedRequests = 0;
        let totalHours = 0;
        let totalEmployees = 0;
        let validatedComplete = 0;
        let validatedWithIssues = 0;
        let pendingValidation = 0;
  
        for (const doc of snapshot.docs) {
          const data = doc.data();
          totalRequests++;
  
          // Count by status
          if (data.status === 'approved') {
            approvedRequests++;
          } else if (data.status === 'rejected') {
            rejectedRequests++;
          } else {
            pendingRequests++;
          }
  
          // Sum hours and employees
          totalHours += data.totalHours || 0;
          totalEmployees += data.totalEmployees || data.employeeIds?.length || 0;
  
          // Count validation status
          if (data.attendanceValidation) {
            if (data.attendanceValidation.validationPending) {
              pendingValidation++;
            } else if (data.attendanceValidation.isValidated) {
              if (data.attendanceValidation.absentCount === 0) {
                validatedComplete++;
              } else {
                validatedWithIssues++;
              }
            }
          } else {
            pendingValidation++;
          }
        }
  
        const avgHoursPerRequest = totalRequests > 0 ? totalHours / totalRequests : 0;
  
        console.log(`✅ Monthly stats: ${totalRequests} requests, ${totalHours.toFixed(1)} hours`);
  
        return {
          totalRequests,
          approvedRequests,
          pendingRequests,
          rejectedRequests,
          totalHours: Math.round(totalHours * 10) / 10,
          totalEmployees,
          avgHoursPerRequest: Math.round(avgHoursPerRequest * 10) / 10,
          validatedComplete,
          validatedWithIssues,
          pendingValidation
        };
  
      } catch (error) {
        console.error('❌ Error fetching monthly stats:', error);
        return {
          totalRequests: 0,
          approvedRequests: 0,
          pendingRequests: 0,
          rejectedRequests: 0,
          totalHours: 0,
          totalEmployees: 0,
          avgHoursPerRequest: 0,
          validatedComplete: 0,
          validatedWithIssues: 0,
          pendingValidation: 0
        };
      }
    }
  
    /**
     * Get weekly trend data for charts
     */
    async getWeeklyTrend(userId: string): Promise<WeeklyTrend[]> {
      try {
        console.log('📊 Fetching weekly trend for user:', userId);
  
        const trends: WeeklyTrend[] = [];
        const today = new Date();
  
        // Get data for last 7 days
        for (let i = 6; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          date.setHours(0, 0, 0, 0);
  
          const nextDate = new Date(date);
          nextDate.setDate(nextDate.getDate() + 1);
  
          const dayQuery = query(
            collection(db, 'overtime_requests'),
            where('requesterId', '==', userId),
            where('overtimeDate', '>=', Timestamp.fromDate(date)),
            where('overtimeDate', '<', Timestamp.fromDate(nextDate))
          );
  
          const snapshot = await getDocs(dayQuery);
  
          let requests = 0;
          let hours = 0;
          let employees = 0;
  
          for (const doc of snapshot.docs) {
            const data = doc.data();
            requests++;
            hours += data.totalHours || 0;
            employees += data.totalEmployees || data.employeeIds?.length || 0;
          }
  
          trends.push({
            day: date.toLocaleDateString('en-US', { weekday: 'short' }),
            date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            requests,
            hours: Math.round(hours * 10) / 10,
            employees
          });
        }
  
        console.log('✅ Weekly trend data loaded');
        return trends;
  
      } catch (error) {
        console.error('❌ Error fetching weekly trend:', error);
        // Return empty trend data
        return Array(7).fill(null).map((_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - (6 - i));
          return {
            day: date.toLocaleDateString('en-US', { weekday: 'short' }),
            date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            requests: 0,
            hours: 0,
            employees: 0
          };
        });
      }
    }
  
    /**
     * Get time ago string
     */
    private getTimeAgo(date: Date): string {
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
  
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  
    /**
     * Get all-time stats for user
     */
    async getAllTimeStats(userId: string): Promise<{
      totalRequests: number;
      totalHours: number;
      totalEmployees: number;
      approvalRate: number;
    }> {
      try {
        const allTimeQuery = query(
          collection(db, 'overtime_requests'),
          where('requesterId', '==', userId)
        );
  
        const snapshot = await getDocs(allTimeQuery);
  
        let totalRequests = 0;
        let approvedCount = 0;
        let totalHours = 0;
        let totalEmployees = 0;
  
        for (const doc of snapshot.docs) {
          const data = doc.data();
          totalRequests++;
          if (data.status === 'approved') approvedCount++;
          totalHours += data.totalHours || 0;
          totalEmployees += data.totalEmployees || data.employeeIds?.length || 0;
        }
  
        const approvalRate = totalRequests > 0 ? (approvedCount / totalRequests) * 100 : 0;
  
        return {
          totalRequests,
          totalHours: Math.round(totalHours * 10) / 10,
          totalEmployees,
          approvalRate: Math.round(approvalRate)
        };
  
      } catch (error) {
        console.error('❌ Error fetching all-time stats:', error);
        return {
          totalRequests: 0,
          totalHours: 0,
          totalEmployees: 0,
          approvalRate: 0
        };
      }
    }
  }
  
  export const dashboardStatsService = new DashboardStatsService();